/**
 * The top bar's two unread counters (parity card **P-168**).
 *
 * The badge used to be painted from the **bootstrap** snapshot — `AppShell` passed
 * `bootstrap.data?.unread` straight into `TopBar`, and `shell.bootstrap` is fetched once with a
 * 60 s `staleTime` — while everything that *changes* the number (the notifications pane's
 * `Mark all as read`, a row opened from the pane, `/my_inbox`) went through
 * `notifications.markAllRead` / `inbox.markAllRead` and touched nothing the badge reads. The
 * rows lost their unread dots, the server agreed (`notifications.unreadCount` answered 0), and
 * the red counter on the bell kept its old number until the page was reloaded.
 *
 * Two sources for one number is the defect, so this removes one of them: the badge reads the
 * **live** procedures, which are the same ones the pane reads, and they are refetched by every
 * `notification.*` event through `useNotificationsRealtime`. `markAllRead` then only has to
 * invalidate these two queries (`invalidateUnreadCounts`), which is one line at the call site
 * and no second copy of the count anywhere.
 */
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from '../lib/trpc';
import { useNotificationsRealtime } from '../modules/notifications';
import { useDemoShell } from '../modules/shell/demo';

export interface UnreadCounts {
  notifications: number;
  inbox: number;
}

/** The query keys the badge reads, so a read-marking mutation can invalidate exactly those. */
export function invalidateUnreadCounts(
  client: QueryClient,
  keys: { notifications: readonly unknown[]; inbox: readonly unknown[] },
): void {
  void client.invalidateQueries({ queryKey: keys.notifications });
  void client.invalidateQueries({ queryKey: keys.inbox });
}

export function useUnreadCounts(fallback: UnreadCounts): UnreadCounts {
  const trpc = useTRPC();
  const demo = useDemoShell();
  const client = useQueryClient();
  /*
   * The socket is the primary trigger: a `notification.created` for this user bumps `revision`,
   * and the effect below re-reads both counts. It costs two tiny queries per event and it can
   * never drift, which the bootstrap snapshot could and did.
   */
  const { revision } = useNotificationsRealtime(!demo);

  const notificationsOptions = trpc.notifications.unreadCount.queryOptions(undefined, {
    enabled: !demo,
    staleTime: 15_000,
  });
  const inboxOptions = trpc.inbox.unreadCount.queryOptions(undefined, {
    enabled: !demo,
    staleTime: 15_000,
  });
  const notifications = useQuery(notificationsOptions);
  const inbox = useQuery(inboxOptions);

  useEffect(() => {
    if (revision === 0 || demo) return;
    invalidateUnreadCounts(client, {
      notifications: notificationsOptions.queryKey,
      inbox: inboxOptions.queryKey,
    });
    // The query keys are stable for a given router; `revision` is the trigger.
  }, [revision, demo, client]);

  return {
    // Until the first answer lands the bootstrap snapshot is still the best number there is.
    notifications: typeof notifications.data === 'number' ? notifications.data : fallback.notifications,
    inbox: typeof inbox.data === 'number' ? inbox.data : fallback.inbox,
  };
}

/** The same two keys, for a call site that has the tRPC proxy but not this hook's queries. */
export function useUnreadQueryKeys(): { notifications: readonly unknown[]; inbox: readonly unknown[] } {
  const trpc = useTRPC();
  return {
    notifications: trpc.notifications.unreadCount.queryKey(),
    inbox: trpc.inbox.unreadCount.queryKey(),
  };
}
